import { useNavigate } from 'react-router-dom'
import { AlertCircle, Truck, Banknote, CalendarClock, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DashboardData } from '@/types/dashboard'

type Tone = 'critical' | 'attention'

interface ActionItem {
  key: string
  count: number
  label: string
  detail?: string
  icon: React.ElementType
  tone: Tone
  href: string
}

interface ActionQueueProps {
  readonly data: DashboardData | undefined
  readonly isLoading: boolean
  /** Operador (sem `reports.view`) só vê itens operacionais (fretes). */
  readonly showFinancialItems: boolean
}

function formatMoney(value: string | number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value))
}

function pluralize(n: number, singular: string, plural: string): string {
  return n === 1 ? singular : plural
}

function computeItems(data: DashboardData, showFinancialItems: boolean): ActionItem[] {
  const items: ActionItem[] = []

  const pendingFreights = data.freights.pending
  if (pendingFreights > 0) {
    items.push({
      key: 'freights-pending',
      count: pendingFreights,
      label: pluralize(pendingFreights, 'frete aguardando motorista', 'fretes aguardando motorista'),
      icon: Truck,
      tone: 'attention',
      href: '/freights?status=PENDING',
    })
  }

  if (!showFinancialItems) return items

  const overdueInvoices = data.invoices.overdueCount ?? 0
  if (overdueInvoices > 0) {
    items.push({
      key: 'invoices-overdue',
      count: overdueInvoices,
      label: pluralize(overdueInvoices, 'fatura vencida', 'faturas vencidas'),
      icon: AlertCircle,
      tone: 'critical',
      href: '/invoices?status=OVERDUE',
    })
  }

  const pendingRepayments = data.repayments.pendingCount
  if (pendingRepayments > 0) {
    items.push({
      key: 'repayments-pending',
      count: pendingRepayments,
      label: pluralize(pendingRepayments, 'repasse pendente', 'repasses pendentes'),
      detail: formatMoney(data.repayments.pendingAmount),
      icon: Banknote,
      tone: 'attention',
      href: '/repayments?status=PENDING',
    })
  }

  const upcomingCount = data.upcomingInvoices?.length ?? 0
  if (upcomingCount > 0) {
    items.push({
      key: 'invoices-upcoming',
      count: upcomingCount,
      label: pluralize(
        upcomingCount,
        'fatura vence nos próximos 7 dias',
        'faturas vencem nos próximos 7 dias',
      ),
      icon: CalendarClock,
      tone: 'attention',
      href: '/invoices',
    })
  }

  return items
}

const TONE_DOT: Record<Tone, string> = {
  critical: 'bg-red-500',
  attention: 'bg-amber-500',
}

const TONE_ICON: Record<Tone, string> = {
  critical: 'text-red-600',
  attention: 'text-amber-600',
}

const TONE_COUNT: Record<Tone, string> = {
  critical: 'text-red-700',
  attention: 'text-amber-700',
}

function ActionRow({ item }: { readonly item: ActionItem }) {
  const navigate = useNavigate()
  const Icon = item.icon
  return (
    <button
      type="button"
      onClick={() => navigate(item.href)}
      className={cn(
        'group flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors',
        'hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <span className={cn('h-2 w-2 shrink-0 rounded-full', TONE_DOT[item.tone])} aria-hidden />
      <Icon size={16} className={cn('shrink-0', TONE_ICON[item.tone])} aria-hidden />
      <span className="flex-1 text-sm text-foreground">
        <span className={cn('font-semibold tabular-nums', TONE_COUNT[item.tone])}>{item.count}</span>{' '}
        {item.label}
        {item.detail && (
          <span className="ml-1 text-muted-foreground">· {item.detail}</span>
        )}
      </span>
      <ChevronRight
        size={16}
        className="shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
        aria-hidden
      />
    </button>
  )
}

function ActionQueueSkeleton() {
  return (
    <div className="space-y-1 rounded-lg border border-border bg-card p-2">
      <div className="h-3 w-32 animate-pulse rounded bg-muted m-2" />
      <div className="space-y-1.5 px-2 pb-1">
        <div className="h-8 w-full animate-pulse rounded bg-muted" />
        <div className="h-8 w-full animate-pulse rounded bg-muted" />
      </div>
    </div>
  )
}

/**
 * Banner no topo do Dashboard listando o que precisa de ação agora:
 * fretes sem motorista, faturas vencidas, repasses pendentes etc.
 * Cada linha leva direto pra lista correspondente já filtrada.
 *
 * Não renderiza quando todas as filas estão vazias — sem "tudo em dia"
 * persistente porque isso passa a ser ruído depois da primeira vez.
 */
export function ActionQueueBanner({ data, isLoading, showFinancialItems }: ActionQueueProps) {
  if (isLoading) return <ActionQueueSkeleton />
  if (!data) return null

  const items = computeItems(data, showFinancialItems)
  if (items.length === 0) return null

  return (
    <section
      aria-label="Pendências da operação"
      className="rounded-lg border border-border bg-card p-2"
    >
      <h2 className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Precisam da sua atenção
      </h2>
      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item.key}>
            <ActionRow item={item} />
          </li>
        ))}
      </ul>
    </section>
  )
}
